import { defineStore, storeToRefs } from 'pinia'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useScriptTag } from '@vueuse/core'
import { getWindow } from '@/helpers/globals'
import { useAuth } from '@/modules/auth'
import { useIntercomSettings } from './useIntercomSettings'
import { useBooleanCookie } from '@/plugins/cookie-plugin'
import { useIntercomAppId } from '@/plugins/intercom'
import { useIsAdmin } from '@/components/app/isAdmin'

const useIntercomStore = defineStore('intercom', () => {
    const isLoaded = ref(false)
    const unreadCount = ref(0)
    const chatStarted = useBooleanCookie('chat_started')

    return {
        isLoaded,
        unreadCount,
        chatStarted,
    }
})

export function useIntercomLauncher() {
    const APP_ID = useIntercomAppId()

    const { load, unload } = useScriptTag(
        `https://widget.intercom.io/widget/${APP_ID}`,
        () => undefined,
        { manual: true }
    )

    const window = getWindow()
    const isAdmin = useIsAdmin()
    const { getIntercomSettings, getIntercomUser } = useIntercomSettings(APP_ID)

    const { isLoaded, unreadCount, chatStarted } =
        storeToRefs(useIntercomStore())

    const startChat = () => {
        chatStarted.value = true
    }

    const stopChat = () => {
        chatStarted.value = false
    }

    let isLoading = false
    const loadIntercom = async () => {
        if (!window) {
            return
        }

        if (isAdmin.value) {
            return Promise.reject('Intercom disabled because you are admin')
        }

        if (isLoaded.value || isLoading) {
            return
        }

        isLoading = true

        const intercomSettings = getIntercomSettings()

        function Intercom(...args: any[]) {
            Intercom.c(args)
        }

        Intercom.q = [] as any[]
        Intercom.c = function (args: any) {
            Intercom.q.push(args)
        }

        window.Intercom = Intercom

        await load()

        isLoaded.value = true
        isLoading = false

        window.Intercom('onShow', startChat)

        window.Intercom('onHide', stopChat)

        window.Intercom('onUnreadCountChange', (count: number) => {
            unreadCount.value = count
        })

        window.Intercom('boot', intercomSettings)
    }

    const show = async () => {
        try {
            await loadIntercom()
            window?.Intercom?.('show')
        } catch (e) {
            alert(e)
        }
    }

    const { user } = useAuth()

    watch(user, (user) => {
        if (isLoaded.value) {
            if (user) {
                window?.Intercom?.('update', getIntercomUser())
            } else {
                window?.Intercom?.('shutdown')

                chatStarted.value = false
            }
        }
    })

    const router = useRouter()
    router.afterEach(() => {
        if (isLoaded.value) {
            window?.Intercom?.('update', {
                last_request_at: new Date().getTime() / 1000,
            })
        }
    })

    onMounted(() => {
        loadIntercom()
            .then(() => {})
            .catch(() => {})
    })

    onUnmounted(() => {
        unload()
    })

    return {
        appId: APP_ID,
        isLoaded,
        unreadCount,
        chatStarted,
        show,
    }
}
